import {Action, Module, Mutation, VuexModule} from 'vuex-module-decorators';
import {SessionStore, mergeAndAdd, IOffer, CustomerProfileStore, ILandingPageOpportunity} from '../../store';
import {IBaseResponse, WebService} from '../../services';
import type {ICampaign, ILandingPageOpportunityOffer} from './interfaces';
import type {TAssetSaveType} from './constants';


interface IFetchCampaignParams {
	campaignId: string;
	check: string;
	includeSaveTypes: TAssetSaveType[];
}

interface ILandingPageViewParams {
	campaignId: ICampaign['id'];
	offerIds: Array<IOffer['id']>;
}
interface ILandingPageClaimOfferParams {
	campaignId: ICampaign['id'];
	offerId: IOffer['id'];
	refetchProfile?: boolean;
}

type TResponseCampaign = IBaseResponse<{campaign: ICampaign[]}>;

// 24 hours as seconds
const OFFER_CLAIM_INTERVAL = 86400;

const OFFER_VIEW_TIME_KEY_PREFIX = 'offerId-';

@Module({name: 'campaigns', namespaced: true})
export class CampaignModule extends VuexModule {
	campaigns: ICampaign[] = [];
	landingPageOpportunities: ILandingPageOpportunity[] = [];

	/**
	 * returns the single campaign matching the given ID and assetSaveType
	 *
	 * By default, this method returns the published asset, if found. If a different
	 * assetSaveType is specified, the method attempts to find it. If no match is
	 * found, null is returned.
	 */
	get campaign() {
		return (id: string | null, assetSaveType: TAssetSaveType = 'published'): ICampaign | null => {
			if(id === null || id === '' || this.campaigns.length === 0) {
				return null;
			}

			const campaignsMatchingID = this.campaigns.filter(c => c.id === id);
			const result = campaignsMatchingID.find(c => assetSaveType === c.assetSaveType);

			if(typeof result !== 'undefined') {
				return result;
			}

			console.warn(`Unable to find campaign matching ID ${id} of type ${assetSaveType}.`);
			return null;
		};
	}

	get landingPageOpportunity() {
		return (campaignId: ICampaign['id']) => {
			return this.landingPageOpportunities.find(opp => opp.campaignId === campaignId);
		};
	}

	get landingPageOpportunityOffer() {
		return (campaignId: ICampaign['id'], landingPageOfferId: IOffer['id']) => {
			return this.landingPageOpportunity(campaignId)?.offers.find(o => o.landingPageOfferId === landingPageOfferId);
		};
	}

	/**
	 * returns timestamp in seconds when given offer will expire
	 */
	get landingPageOpportunityOfferExpireTime() {
		return (campaignId: ICampaign['id'], offerId: IOffer['id']): number => {
			const offerViewTime = this.landingPageOpportunityOfferViewTime(campaignId, offerId);
			if(offerViewTime) {
				return offerViewTime + OFFER_CLAIM_INTERVAL;
			}

			return 0;
		};
	}

	/**
	 * returns timestamp in seconds when given offer was first viewed
	 */
	get landingPageOpportunityOfferViewTime() {
		return (campaignId: ICampaign['id'], offerId: IOffer['id']): number => {
			const landingPageOpportunity = this.landingPageOpportunity(campaignId);
			if(landingPageOpportunity) {
				const key = OFFER_VIEW_TIME_KEY_PREFIX + offerId.toString();
				return landingPageOpportunity.offerViewTimes?.[key];
			}

			return 0;
		};
	}

	@Action
	async fetchCampaign(params: IFetchCampaignParams) {
		const fetchParams: Record<string, string> = {
			campaignId: params.campaignId,
			check: params.check,
			franchisorBase: SessionStore.brandConfig?.id ?? ''
		};

		if(typeof params.includeSaveTypes !== 'undefined') {
			fetchParams.includeSaveTypes = JSON.stringify(params.includeSaveTypes);
		}

		const result = await WebService.unauthenticatedClient.get<TResponseCampaign>(
			'campaign', {
				params: {...fetchParams}
			}
		);

		this.addCampaigns(result.data.data.campaign);
	}

	@Action
	async landingPageView(params: ILandingPageViewParams) {
		const result = await WebService.authenticatedClient.post<IBaseResponse<{landingPageOpportunity: ILandingPageOpportunity}>>(
			'landing_page_view',
			{
				campaignId: params.campaignId,
				offerIds: params.offerIds
			}
		);
		this.addLandingPageOpportunity(result.data.data.landingPageOpportunity);
	}

	@Action
	async landingPageRedeemOffer(params: ILandingPageClaimOfferParams) {
		const result = await WebService.authenticatedClient.post<IBaseResponse<{result: {offer: IOffer; landingPageOpportunityOffer: ILandingPageOpportunityOffer}}>>(
			'landing_page_claim_offer',
			{
				campaignId: params.campaignId,
				offerId: params.offerId
			}
		);

		CustomerProfileStore.addOffer(result.data.data.result.offer);
		this.addLandingPageOpportunityOffer(result.data.data.result.landingPageOpportunityOffer);
		if(params?.refetchProfile === true) {
			await CustomerProfileStore.fetchProfile();
		}
	}

	@Mutation
	addCampaigns(campaigns: ICampaign[]) {
		mergeAndAdd(this.campaigns, campaigns, ['id', 'assetSaveType']);
	}

	@Mutation
	addLandingPageOpportunity(landingPageOpportunity: ILandingPageOpportunity) {
		mergeAndAdd(this.landingPageOpportunities, [landingPageOpportunity], ['campaignId']);
	}

	@Mutation
	addLandingPageOpportunityOffer(landingPageOpportunityOffer: ILandingPageOpportunityOffer) {
		// Ideally could call the getter, but getters don't work in mutations
		const landingPageOpportunity = this.landingPageOpportunities.find(opp => opp.campaignId === landingPageOpportunityOffer.campaignId);
		if(landingPageOpportunity) {
			mergeAndAdd(landingPageOpportunity.offers, [landingPageOpportunityOffer], ['landingPageOfferId']);
		}
	}
}
